import React, { useRef, useState } from 'react';
import { T, S, Modal } from '../styles/ds';
import { logoutSession } from '../services/api';
import { getQueued } from '../services/offlineQueue';
import { readKnownShops, otherKnownShops } from '../utils/knownShops';
import { switchToShop, switchErrorText, reloadForPin } from '../utils/shopSwitch';

// PH-50 — "switch shop", for a staff member who covers two shops on one device. Lists only the
// shops this device has already been opened for by their own link (utils/knownShops.js), and
// moves between them by a full navigation to that shop's link (utils/shopSwitch.js) — so the new
// shop always starts at its own PIN screen, with nothing of the old shop left in memory.
//
// Renders nothing at all until the device knows at least one OTHER shop: a one-shop device (every
// shop today) sees no change.
export default function ShopSwitcher({ currentSlug, tone = 'light', deps }) {
  // Read once per mount. The list only changes when a shop's link is opened, which is a page load.
  const [others] = useState(() => {
    try { return otherKnownShops(readKnownShops(window.localStorage), currentSlug); } catch (_) { return []; }
  });
  const [open, setOpen] = useState(false);
  const [busySlug, setBusySlug] = useState(null);
  const [error, setError] = useState('');
  const [signedOut, setSignedOut] = useState(false);
  // A ref, not just state: two clicks in the same tick (or a StrictMode double-invoke) would both
  // read `busySlug === null` from the same render and start two switches.
  const busyRef = useRef(false);

  if (others.length === 0) return null;

  const pick = async (slug) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusySlug(slug);
    setError('');
    try {
      await switchToShop(slug, { getQueued, logout: logoutSession, ...deps });
      // On success the page is navigating away; leave the busy state on so nothing else is pressed.
    } catch (err) {
      busyRef.current = false;
      setBusySlug(null);
      setError(switchErrorText(err));
      setSignedOut(Boolean(err && err.signedOut));
    }
  };

  const dark = tone === 'dark';
  return (
    <>
      <button
        type="button"
        onClick={() => { setError(''); setOpen(true); }}
        style={{ ...S.tap, ...(dark ? S.switchChipDark : S.switchChip) }}
      >
        🔁 दुकान बदलें / Switch shop
      </button>
      {open && (
        <Modal
          title="दुकान बदलें / Switch shop"
          subtitle="इस डिवाइस पर पहले खोली गई दुकानें। दुकान बदलने पर आप इस दुकान से लॉग आउट हो जाएँगे और दूसरी दुकान का PIN डालना होगा। / Shops already opened on this device. Switching logs you out of this shop; the other shop asks for its own PIN."
          onClose={() => { if (!busySlug) setOpen(false); }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {others.map((shop) => {
              const busy = busySlug === shop.slug;
              return (
                <button
                  key={shop.slug}
                  type="button"
                  disabled={Boolean(busySlug)}
                  onClick={() => pick(shop.slug)}
                  style={{
                    ...S.btnSec,
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                    textAlign: 'left', whiteSpace: 'normal', color: T.ink,
                    cursor: busySlug ? 'not-allowed' : 'pointer',
                    opacity: busySlug && !busy ? 0.55 : 1,
                  }}
                >
                  <span style={S.switchRowName}>
                    {busy ? 'बदल रहे हैं… / Switching…' : (shop.name || 'नाम अभी पता नहीं / Name not known yet')}
                  </span>
                  <span style={S.switchRowSlug}>{shop.slug}</span>
                </button>
              );
            })}
            {error && (
              <div role="alert" style={S.alertBox}>
                <div style={S.alertDetail}>{error}</div>
                {signedOut && (
                  // reloadForPin, not a plain reload: if the saved session could not be removed
                  // after the server session was ended, only the per-window flag keeps App from
                  // restoring it (Codex review, PR #87).
                  <button type="button" onClick={() => reloadForPin()} style={S.btnPri}>
                    फिर से लोड करें / Reload
                  </button>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

// Shown INSTEAD of the whole app when another window on this device switched it to a different
// shop (App.jsx's `storage` listener). Nothing of this window's shop stays mounted behind it, so no
// page can make one more call under the other shop's login; the only way on is a reload that asks
// for a PIN again.
export function ShopChangedElsewhere({ onReload = reloadForPin }) {
  return (
    <div style={{
      minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 24, fontFamily: T.font,
    }}>
      <div role="alert" style={{ ...S.cardPad, maxWidth: 440, textAlign: 'center' }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>🔁</div>
        <div style={{ ...S.h2, marginBottom: 8 }}>
          इस डिवाइस पर दूसरी दुकान खोली गई है / Another shop was opened on this device
        </div>
        <div style={{ fontSize: 13, color: T.sec, lineHeight: 1.5, marginBottom: 16 }}>
          किसी दूसरी विंडो में दूसरी दुकान खुली है। दोनों दुकानों का हिसाब अलग रखने के लिए यह विंडो रोक दी गई है —
          फिर से लोड करें और PIN डालें। / A different shop was opened in another window. To keep the two
          shops' records apart this window has stopped — reload it and enter a PIN.
        </div>
        <button type="button" onClick={() => onReload()} style={S.btnPri}>
          फिर से लोड करें / Reload
        </button>
      </div>
    </div>
  );
}
